/**
 * Sanitize author-supplied CSS before injecting it into a <style> tag.
 *
 * Previously wrapped the CSS in <style> and handed it to DOMPurify. That could not run on the
 * server -- `import { sanitize } from 'dompurify'` is `undefined` without a DOM -- and threw during
 * SSR, which abandoned the HTML render for the entire page. This is a pure-JS equivalent, so it
 * behaves identically on the server and in the browser.
 *
 * The goals are the same as before: prevent a breakout from the <style> element, and strip the
 * constructs that turn CSS into a script or a network call.
 */
export function sanitizeCss(css: string): string {
  if (!css) return '';

  return (
    css
      // Comments first: they can hide any of the patterns below (e.g. `expr/*x*/ession(`).
      .replace(/\/\*[\s\S]*?\*\//g, '')
      // A literal </style> (or any tag close) would end the element and start markup.
      .replace(/<\s*\/?\s*[a-zA-Z][^>]*>/g, '')
      // Legacy IE script execution.
      .replace(/expression\s*\(/gi, '')
      // javascript:/vbscript: URLs inside url() or content.
      .replace(/(javascript|vbscript)\s*:/gi, '')
      // @import pulls in a stylesheet from anywhere.
      .replace(/@import[^;]*;?/gi, '')
      // -moz-binding loaded remote XBL.
      .replace(/-moz-binding\s*:/gi, '')
      // Remote url() -- keep data: and same-origin relative URLs, drop absolute/protocol-relative.
      .replace(/url\s*\(\s*(['"]?)\s*(?:https?:)?\/\/[^)]*\)/gi, 'url($1$1)')
      .trim()
  );
}
